import classNames from "classnames";
import CardAdd from "components/billing/CardAdd";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { FilingsTimeline } from "components/FilingsTimeline/FilingsTimeline";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { entitiesSortedByCountry } from "utils/entitiesSortedByCountry";
import FreeSubscriptionModal from "components/tasks/task7004/FreeSubscriptionModal";
import { motion } from "framer-motion";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeCardAddModal,
  closeFreeSubscription,
} from "store/slices/subscriptionModal";
import { RootState } from "store/store";
import authContext from "../../jwt_context&axios/authContext";
import "static/styles/components/dashboardHome.css";
import "static/styles/components/home.css";
import WelcomeUser from "../design/welcomeUser";
import { ForeignCAHome } from "./ForeignCAHome";
import { TasksActions } from "./TaxComponents/TasksOverview/TasksOverview";
import { EntityScopeSelector } from "./EntityScopeSelector";
import { FreeFilingBanner } from "./FreeFilingBanner";

export const LargeCard = ({
  children,
  block,
}: {
  children: ReactNode;
  block?: boolean;
}) => (
  <motion.div
    layout
    className={classNames(
      "t-w-full hover:t-shadow-light-30 t-transition-shadow t-rounded-lg t-overflow-hidden",
      // block lets the content set the height, so it fills its cell instead
      block ? "t-h-full" : "md:t-h-[200px]"
    )}
  >
    {children}
  </motion.div>
);

export const Home = () => {
  usePageTitle("Home");
  const dispatch = useDispatch();
  const { authtoken } = useContext(authContext);
  const { isCustomer, isForeignCA } = useRoleBasedView();

  const { entities: allEntities } = useCurrentGroupContext();

  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  // The timeline is US filings, so a non-US entity is never in scope — not in
  // the selector, and not in the deadlines behind it.
  const { usEntities } = entitiesSortedByCountry(allEntities);

  // Nothing selected means every US entity — the selector reads "All entities".
  const scopedEntityIds =
    selectedEntityIds.length > 0
      ? selectedEntityIds
      : usEntities.map((entity) => entity.uuid);

  const { isOpenFreeSubscription, isOpenCardAddModal } = useSelector(
    (state: RootState) => state.subscriptionModal
  );

  return (
    <div className="t-w-full">
      {isForeignCA && <ForeignCAHome />}
      {isCustomer ? (
        <DashboardLayout
          header={
            <Header
              v2
              className="!t-border-0"
              title={`Welcome ${authtoken?.first_name}`}
              right={
                // One entity is no choice at all — the control goes entirely.
                usEntities.length > 1 ? (
                  <div className="sm:t-hidden">
                    <EntityScopeSelector
                      selectedEntityIds={selectedEntityIds}
                      onChange={setSelectedEntityIds}
                    />
                  </div>
                ) : null
              }
            />
          }
        >
          <motion.div layout className="t-flex t-flex-col t-gap-6 t-pb-8">
            <FreeFilingBanner />
            {/* Entities arrive a beat after first paint; asking before then
                  fetches the whole group and then refetches. */}
            {scopedEntityIds.length > 0 && (
              <div className="sm:t-hidden">
                <FilingsTimeline entityIds={scopedEntityIds} />
              </div>
            )}
            <TasksActions
              entityIds={scopedEntityIds}
              isScopeReady={usEntities.length > 0}
            />
          </motion.div>

          <FreeSubscriptionModal
            openSubscription={isOpenFreeSubscription}
            closeSubscription={() => dispatch(closeFreeSubscription())}
          />
          {isOpenCardAddModal && (
            <CardAdd
              openAddCard={isOpenCardAddModal}
              closeAddCard={() => dispatch(closeCardAddModal())}
            />
          )}
        </DashboardLayout>
      ) : (
        <WelcomeUser />
      )}
    </div>
  );
};
